import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  CircularProgress,
  Box,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  OutlinedInput
} from "@mui/material";
import Popup from "reactjs-popup";
import { Calendar } from "@hassanmojab/react-modern-calendar-datepicker";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import GroupIcon from "@mui/icons-material/Group";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { CalendarAnchor } from "../appointmentPanel/AppointmentPanel.styled";
import { ptBrCustomLocale } from "../../constants/Objects";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import { GeneralContainer } from "../../components/UI/generalLayout/Layout.styled";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import Colors from "../../constants/Colors";
import { ReaplicacaoService } from "../../core/http/service/ReaplicacaoService";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import moment from "moment";

const ReaplicacaoQuestionario = () => {
  const { currentCompany } = useSelector((state: any) => state.login);
  const { isMobile } = useResponsiveInfo();

  const meses = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  const [selectedDay, setSelectedDay] = useState<any>({
    year: moment().year(),
    month: moment().month() + 1,
    day: moment().date()
  });

  const [selectedPostos, setSelectedPostos] = useState<string[]>([]);
  const [postoOptions, setPostoOptions] = useState<{ label: string }[]>([]);
  const [historico, setHistorico] = useState<any[]>([]);

  const [isLoadingPostos, setIsLoadingPostos] = useState<boolean>(false);
  const [isLoadingHistorico, setIsLoadingHistorico] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  // Confirmation dialog for toggle status
  const [toggleConfirmRow, setToggleConfirmRow] = useState<any | null>(null);

  // Modal for viewing impacted users
  const [impactedModalOpen, setImpactedModalOpen] = useState<boolean>(false);
  const [selectedRowDetail, setSelectedRowDetail] = useState<any | null>(null);
  const [impactedUsersData, setImpactedUsersData] = useState<any | null>(null);
  const [isLoadingImpactedUsers, setIsLoadingImpactedUsers] = useState<boolean>(false);
  const [userSearchQuery, setUserSearchQuery] = useState<string>("");
  const [userFilterTab, setUserFilterTab] = useState<number>(0);

  const service = new ReaplicacaoService(currentCompany?.id ?? "");

  const formatSelectedDay = () => {
    return `${selectedDay.day} de ${meses[selectedDay.month - 1]} de ${selectedDay.year}`;
  };

  const loadPostos = async () => {
    if (!currentCompany?.id) return;
    setIsLoadingPostos(true);
    try {
      const response: any = await service.getPostosTrabalho();
      if (response && response.data && response.data.data) {
        setPostoOptions(response.data.data.map((p: string) => ({ label: p })));
      }
    } catch (err) {
      console.error("Erro ao carregar postos de trabalho", err);
    } finally {
      setIsLoadingPostos(false);
    }
  };

  const loadHistorico = async () => {
    if (!currentCompany?.id) return;
    setIsLoadingHistorico(true);
    try {
      const response: any = await service.getHistorico();
      if (response && response.data && response.data.data) {
        setHistorico(response.data.data);
      }
    } catch (err) {
      console.error("Erro ao carregar histórico", err);
    } finally {
      setIsLoadingHistorico(false);
    }
  };

  useEffect(() => {
    loadPostos();
    loadHistorico();
  }, [currentCompany?.id]);

  const handleSave = async () => {
    setErrorMsg("");
    setSuccessMsg("");

    if (!selectedDay) {
      setErrorMsg("Selecione a Data de Corte.");
      return;
    }

    if (selectedPostos.length === 0) {
      setErrorMsg("Selecione pelo menos um Posto de Trabalho.");
      return;
    }

    const formattedDate = `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(selectedDay.day).padStart(2, '0')}`;

    setIsSaving(true);
    try {
      const response: any = await service.salvarParametrizacao({
        dataCorte: formattedDate,
        postosTrabalho: selectedPostos
      });

      if (response && response.data && response.data.success) {
        setSuccessMsg("Parametrização salva com sucesso!");
        setSelectedDay({
          year: moment().year(),
          month: moment().month() + 1,
          day: moment().date()
        });
        setSelectedPostos([]);
        loadHistorico();
      } else {
        const errors = response?.data?.errors?.join(", ") || "Erro inesperado ao salvar.";
        setErrorMsg(errors);
      }
    } catch (err: any) {
      setErrorMsg("Falha de conexão ou erro no servidor ao salvar parametrização.");
    } finally {
      setIsSaving(false);
    }
  };

  const confirmToggleStatus = async () => {
    if (!toggleConfirmRow) return;
    const row = toggleConfirmRow;
    setToggleConfirmRow(null);
    setTogglingId(row.id);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const response: any = await service.toggleStatus(row.id);
      if (response && response.data && response.data.success) {
        setSuccessMsg(`Parametrização ${row.ativo ? "inativada" : "ativada"} com sucesso!`);
        loadHistorico();
      } else {
        const errors = response?.data?.errors?.join(", ") || "Erro ao alterar status da parametrização.";
        setErrorMsg(errors);
      }
    } catch (err) {
      setErrorMsg("Falha ao comunicar com o servidor para atualizar status.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenImpactedUsers = async (row: any) => {
    setSelectedRowDetail(row);
    setImpactedModalOpen(true);
    setIsLoadingImpactedUsers(true);
    setUserSearchQuery("");
    setUserFilterTab(0);
    try {
      const response: any = await service.getUsuariosImpactados(row.id);
      if (response && response.data && response.data.data) {
        setImpactedUsersData(response.data.data);
      } else {
        setImpactedUsersData({
          dataCorte: moment(row.dataCorte, 'YYYY-MM-DD').format('DD/MM/YYYY'),
          postosTrabalho: row.postosTrabalho,
          totalImpactados: row.impactados || 0,
          totalResponderam: row.responderam || 0,
          usuarios: []
        });
      }
    } catch (err) {
      console.error("Erro ao carregar usuários impactados", err);
      setImpactedUsersData({
        dataCorte: moment(row.dataCorte, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        postosTrabalho: row.postosTrabalho,
        totalImpactados: row.impactados || 0,
        totalResponderam: row.responderam || 0,
        usuarios: []
      });
    } finally {
      setIsLoadingImpactedUsers(false);
    }
  };

  const filteredUsers = (impactedUsersData?.usuarios || []).filter((user: any) => {
    const matchesSearch =
      user.nome?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.cpf?.includes(userSearchQuery) ||
      user.postoTrabalho?.toLowerCase().includes(userSearchQuery.toLowerCase());

    if (!matchesSearch) return false;

    if (userFilterTab === 1) return user.realizouNovamente;
    if (userFilterTab === 2) return !user.realizouNovamente;
    return true;
  });

  const PopupCalendar = () => {
    return (
      <Popup
        position="bottom center"
        nested
        trigger={
          <Box
            sx={{
              height: "42px",
              border: `1px solid ${Colors.GRAY300}`,
              borderRadius: "8px",
              padding: "0 12px",
              width: '100%',
              backgroundColor: '#FAFAFA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: Colors.SPM600,
                backgroundColor: '#FFF'
              }
            }}
          >
            <Box display="flex" alignItems="center" gap={1} overflow="hidden">
              <CalendarTodayIcon style={{ color: Colors.SPM600, fontSize: '18px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: Colors.GRAY900, fontSize: "0.85rem", fontWeight: 500, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                {formatSelectedDay()}
              </Typography>
            </Box>
            <KeyboardArrowDownIcon style={{ color: Colors.GRAY600, fontSize: '20px', flexShrink: 0 }} />
          </Box>
        }
      >
        <Calendar
          value={selectedDay}
          onChange={setSelectedDay}
          locale={ptBrCustomLocale}
          colorPrimary={Colors.SPM600}
          colorPrimaryLight={Colors.SPM50}
          shouldHighlightWeekends
        />
      </Popup>
    );
  };

  const renderPostosChipsModal = (postos: string[] = []) => {
    if (!postos || postos.length === 0) return null;
    if (postos.length <= 2) {
      return (
        <Box display="inline-flex" flexWrap="wrap" gap={0.5} ml={1} sx={{ verticalAlign: 'middle' }}>
          {postos.map((p, idx) => (
            <Chip key={idx} label={p} size="small" sx={{ backgroundColor: Colors.SPM50, color: Colors.SPM600, fontWeight: 600, fontSize: '0.75rem' }} />
          ))}
        </Box>
      );
    }
    const visible = postos.slice(0, 2);
    const hiddenCount = postos.length - 2;
    const remainingText = postos.slice(2).join(", ");

    return (
      <Box display="inline-flex" alignItems="center" gap={0.5} ml={1} sx={{ verticalAlign: 'middle' }}>
        {visible.map((p, idx) => (
          <Chip key={idx} label={p} size="small" sx={{ backgroundColor: Colors.SPM50, color: Colors.SPM600, fontWeight: 600, fontSize: '0.75rem' }} />
        ))}
        <Tooltip title={`Outros postos: ${remainingText}`} arrow placement="top">
          <Chip 
            label={`+${hiddenCount} outros`} 
            size="small" 
            sx={{ backgroundColor: Colors.GRAY200, color: Colors.GRAY800, fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }} 
          />
        </Tooltip>
      </Box>
    );
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <ScreenHeader
          hasRightButton={false}
          screenTitle="Reaplicar Questionário"
          rightButtonText=""
          onRightButtonClick={() => {}}
          errors={[]}
          onCloseError={() => {}}
        />

        {/* Subtitle & Info Header */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '10px', border: `1px solid ${Colors.GRAY200}`, backgroundColor: '#fff' }}>
          <Typography variant="body2" sx={{ color: Colors.GRAY700, lineHeight: 1.6, fontSize: '0.9rem' }}>
            Defina a <strong>Data de Corte</strong> e os <strong>Postos de Trabalho</strong> para reaplicação obrigatória dos questionários (OMS e SPM). Colaboradores e dependentes (18+ anos) vinculados a estes postos que preencheram questionários na ou antes da data de corte precisarão respondê-los novamente.
          </Typography>
        </Paper>

        {errorMsg && <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }} onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: '8px' }} onClose={() => setSuccessMsg("")}>{successMsg}</Alert>}

        {/* Form Card */}
        <Card elevation={0} sx={{ borderRadius: '12px', mb: 3.5, border: `1px solid ${Colors.GRAY200}`, backgroundColor: '#fff', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.02)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1.2} mb={3} pb={1.5} sx={{ borderBottom: `1px solid ${Colors.GRAY100}` }}>
              <AddCircleOutlineIcon style={{ color: Colors.SPM600, fontSize: '22px' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: Colors.GRAY900, fontSize: '1rem' }}>
                Nova Parametrização de Reaplicação
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '280px 1fr 210px' },
                columnGap: { xs: '20px', md: '36px' },
                rowGap: '20px',
                alignItems: 'end',
                width: '100%'
              }}
            >
              <Box sx={{ width: '100%', '& .popup-trigger': { width: '100% !important', display: 'block !important' } }}>
                <FormControl fullWidth>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: Colors.GRAY700, mb: 1, display: 'block', fontSize: '0.85rem' }}>
                    Data de Corte *
                  </Typography>
                  <PopupCalendar />
                </FormControl>
              </Box>

              <Box sx={{ width: '100%', minWidth: 0 }}>
                <FormControl fullWidth>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: Colors.GRAY700, mb: 1, display: 'block', fontSize: '0.85rem' }}>
                    Posto(s) de Trabalho *
                  </Typography>
                  {isLoadingPostos ? (
                    <Box display="flex" alignItems="center" height="42px" px={2} sx={{ border: `1px solid ${Colors.GRAY300}`, borderRadius: '8px', backgroundColor: '#FAFAFA', boxSizing: 'border-box' }}>
                      <CircularProgress size={18} sx={{ mr: 1, color: Colors.SPM600 }} /> 
                      <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>Carregando postos...</Typography>
                    </Box>
                  ) : (
                    <Select
                      multiple
                      displayEmpty
                      value={selectedPostos}
                      onChange={(e) => setSelectedPostos(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
                      input={<OutlinedInput sx={{ height: '42px', borderRadius: '8px', backgroundColor: '#FAFAFA', boxSizing: 'border-box' }} />}
                      renderValue={(selected: string[]) => {
                        if (selected.length === 0) {
                          return <span style={{ color: Colors.GRAY500, fontSize: '0.85rem' }}>Selecione o(s) posto(s)...</span>;
                        }
                        if (selected.length <= 2) {
                          return (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" sx={{ backgroundColor: Colors.SPM50, color: Colors.SPM600, fontWeight: 600, fontSize: '0.75rem' }} />
                              ))}
                            </Box>
                          );
                        }
                        return (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: Colors.SPM600, fontSize: '0.85rem' }}>
                            {selected.length} postos selecionados
                          </Typography>
                        );
                      }}
                    >
                      {postoOptions.map((option) => (
                        <MenuItem key={option.label} value={option.label}>
                          <Checkbox checked={selectedPostos.indexOf(option.label) > -1} sx={{ color: Colors.SPM600, '&.Mui-checked': { color: Colors.SPM600 } }} />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Button
                  variant="contained"
                  disabled={isSaving}
                  onClick={handleSave}
                  startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
                  fullWidth
                  sx={{ 
                    borderRadius: '8px', 
                    height: "42px", 
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    backgroundColor: Colors.SPM600,
                    color: "white",
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    '&:hover': {
                      backgroundColor: Colors.SPM700,
                      boxShadow: 'none'
                    }
                  }}
                >
                  {isSaving ? "Salvando..." : "Salvar Configuração"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* History List Card */}
        <Card elevation={0} sx={{ borderRadius: '10px', border: `1px solid ${Colors.GRAY200}`, backgroundColor: '#fff' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <WorkOutlineIcon style={{ color: Colors.SPM600, fontSize: '20px' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: Colors.GRAY900 }}>
                  Histórico de Parametrizações
                </Typography>
                <Chip 
                  label={`${historico.length}`} 
                  size="small" 
                  sx={{ backgroundColor: Colors.GRAY100, color: Colors.GRAY700, fontWeight: 700, ml: 0.5, height: '22px', fontSize: '0.75rem' }} 
                />
              </Box>

              <Tooltip title="Atualizar Histórico">
                <IconButton onClick={loadHistorico} size="small" sx={{ border: `1px solid ${Colors.GRAY200}`, borderRadius: '8px' }}>
                  <RefreshIcon style={{ color: Colors.GRAY600, fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            </Box>

            {isLoadingHistorico ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={5} flexDirection="column" gap={1.5}>
                <CircularProgress style={{ color: Colors.SPM600 }} size={28} />
                <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>Carregando histórico...</Typography>
              </Box>
            ) : historico.length > 0 ? (
              <TableContainer sx={{ border: `1px solid ${Colors.GRAY200}`, borderRadius: "8px", overflow: "hidden" }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: Colors.GRAY50 }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }}>Data do Cadastro</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }}>Data de Corte</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }}>Postos de Trabalho</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }} align="center">Impactados / Responderam</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }}>Cadastrado por</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, py: 1.2, fontSize: '0.85rem' }} align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historico.map((row) => (
                      <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ color: Colors.GRAY700, fontSize: '0.85rem' }}>
                          {row.dataParametrizacao}
                        </TableCell>
                        <TableCell sx={{ color: Colors.GRAY900, fontWeight: 700, fontSize: '0.85rem' }}>
                          {moment(row.dataCorte, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell sx={{ color: Colors.GRAY700 }}>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {row.postosTrabalho.map((posto: string, idx: number) => (
                              <Chip
                                key={idx}
                                label={posto}
                                size="small"
                                sx={{
                                  backgroundColor: Colors.SPM50,
                                  color: Colors.SPM600,
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  borderRadius: "4px"
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: Colors.GRAY900, fontSize: '0.85rem' }}>
                              {row.impactados ?? 0} impactados
                            </Typography>
                            <Typography variant="caption" sx={{ color: (row.responderam || 0) > 0 ? Colors.SPM600 : Colors.GRAY500, fontWeight: 600, fontSize: '0.75rem' }}>
                              {row.responderam ?? 0} já responderam
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: Colors.GRAY700, fontSize: '0.85rem' }}>
                          {row.usuario}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={row.ativo ? "Clique para inativar" : "Clique para ativar"}>
                            <Chip
                              clickable
                              disabled={togglingId === row.id}
                              label={togglingId === row.id ? "Alterando..." : (row.ativo ? "Ativo" : "Inativo")}
                              onClick={() => setToggleConfirmRow(row)}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                backgroundColor: row.ativo ? '#E6F4EA' : '#F1F3F4',
                                color: row.ativo ? '#137333' : '#5F6368',
                                border: `1px solid ${row.ativo ? '#CEEAD6' : '#DADCE0'}`,
                                '&:hover': {
                                  backgroundColor: row.ativo ? '#CEEAD6' : '#E8EAED'
                                }
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                            <Tooltip title="Visualizar Usuários Impactados">
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleOpenImpactedUsers(row)}
                                startIcon={<VisibilityIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  color: Colors.SPM600,
                                  px: 1,
                                  minWidth: 'auto',
                                  '&:hover': {
                                    backgroundColor: Colors.SPM50
                                  }
                                }}
                              >
                                Impactados
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                py={5} 
                sx={{ border: `1px dashed ${Colors.GRAY300}`, borderRadius: "8px", backgroundColor: Colors.GRAY50 }}
              >
                <AssignmentIndIcon sx={{ fontSize: 40, color: Colors.GRAY400, mb: 1 }} />
                <Typography variant="body2" sx={{ color: Colors.GRAY600, fontWeight: 600 }}>
                  Nenhuma parametrização registrada
                </Typography>
                <Typography variant="caption" sx={{ color: Colors.GRAY500 }}>
                  Utilize o formulário acima para cadastrar uma nova data de corte e postos de trabalho.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog for Toggle Status */}
        <Dialog 
          open={Boolean(toggleConfirmRow)} 
          onClose={() => setToggleConfirmRow(null)}
          PaperProps={{ sx: { borderRadius: '12px', p: 1, maxWidth: 440 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: Colors.GRAY900, pb: 1, fontSize: '1.05rem' }}>
            {toggleConfirmRow?.ativo ? "Inativar Parametrização?" : "Ativar Parametrização?"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: Colors.GRAY700, lineHeight: 1.5, fontSize: '0.9rem' }}>
              Tem certeza que deseja {toggleConfirmRow?.ativo ? "inativar" : "ativar"} a parametrização de reaplicação com Data de Corte em <strong>{moment(toggleConfirmRow?.dataCorte, 'YYYY-MM-DD').format('DD/MM/YYYY')}</strong>?
            </Typography>
            {toggleConfirmRow?.ativo && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px', fontSize: '0.8rem', py: 0.5 }}>
                Novos logins em postos desta parametrização deixarão de exigir a reaplicação do questionário.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setToggleConfirmRow(null)} 
              sx={{ color: Colors.GRAY600, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmToggleStatus} 
              variant="contained" 
              sx={{ 
                borderRadius: '8px', 
                fontWeight: 600, 
                textTransform: 'none',
                fontSize: '0.85rem',
                backgroundColor: toggleConfirmRow?.ativo ? '#D93025' : Colors.SPM600,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: toggleConfirmRow?.ativo ? '#B31412' : Colors.SPM700,
                  boxShadow: 'none'
                }
              }}
            >
              Confirmar {toggleConfirmRow?.ativo ? "Inativação" : "Ativação"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal for Viewing Impacted Users */}
        <Dialog
          open={impactedModalOpen}
          onClose={() => setImpactedModalOpen(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: '14px', minHeight: '480px' } }}
        >
          <DialogTitle sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ p: 1, backgroundColor: Colors.SPM50, borderRadius: '8px', display: 'flex' }}>
                <GroupIcon style={{ color: Colors.SPM600, fontSize: '20px' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: Colors.GRAY900, lineHeight: 1.2 }}>
                  Usuários Impactados pela Parametrização
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={0.3}>
                  <Typography variant="caption" sx={{ color: Colors.GRAY600, fontWeight: 500 }}>
                    Corte: <strong>{moment(selectedRowDetail?.dataCorte, 'YYYY-MM-DD').format('DD/MM/YYYY')}</strong> | Postos:
                  </Typography>
                  {renderPostosChipsModal(selectedRowDetail?.postosTrabalho)}
                </Box>
              </Box>
            </Box>
            <IconButton onClick={() => setImpactedModalOpen(false)} size="small">
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 2.5, py: 1 }}>
            {/* Stat Cards inside Modal */}
            <Grid container spacing={2} mb={2.5}>
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${Colors.GRAY200}`, borderRadius: '8px', backgroundColor: '#fff' }}>
                  <Typography variant="caption" sx={{ color: Colors.GRAY600, fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                    Total de Impactados
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: Colors.GRAY900, mt: 0.2 }}>
                    {impactedUsersData?.totalImpactados ?? (selectedRowDetail?.impactados || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 1.5, border: `1px solid #CEEAD6`, borderRadius: '8px', backgroundColor: '#E6F4EA' }}>
                  <Typography variant="caption" sx={{ color: '#137333', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                    Preencheram Novamente
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#137333', mt: 0.2 }}>
                    {impactedUsersData?.totalResponderam ?? (selectedRowDetail?.responderam || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 1.5, border: `1px solid #FAD2CF`, borderRadius: '8px', backgroundColor: '#FCE8E6' }}>
                  <Typography variant="caption" sx={{ color: '#C5221F', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                    Pendentes
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#C5221F', mt: 0.2 }}>
                    {(impactedUsersData?.totalImpactados ?? (selectedRowDetail?.impactados || 0)) - (impactedUsersData?.totalResponderam ?? (selectedRowDetail?.responderam || 0))}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Filter Tabs & Search */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1.5}>
              <Tabs
                value={userFilterTab}
                onChange={(_, val) => setUserFilterTab(val)}
                sx={{
                  minHeight: '32px',
                  '& .MuiTab-root': {
                    minHeight: '32px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.2,
                    mr: 0.5
                  },
                  '& .Mui-selected': {
                    backgroundColor: Colors.SPM50,
                    color: `${Colors.SPM600} !important`
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  }
                }}
              >
                <Tab label={`Todos (${impactedUsersData?.usuarios?.length || 0})`} />
                <Tab label={`Preencheram (${(impactedUsersData?.usuarios || []).filter((u: any) => u.realizouNovamente).length})`} />
                <Tab label={`Pendentes (${(impactedUsersData?.usuarios || []).filter((u: any) => !u.realizouNovamente).length})`} />
              </Tabs>

              <TextField
                placeholder="Buscar por nome, CPF ou posto..."
                size="small"
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: Colors.GRAY500, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '8px', height: '34px', fontSize: '0.8rem', width: 240 }
                }}
              />
            </Box>

            {/* Table of Impacted Users */}
            {isLoadingImpactedUsers ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={5} flexDirection="column" gap={1}>
                <CircularProgress style={{ color: Colors.SPM600 }} size={24} />
                <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>Buscando lista de usuários impactados...</Typography>
              </Box>
            ) : filteredUsers.length > 0 ? (
              <TableContainer sx={{ border: `1px solid ${Colors.GRAY200}`, borderRadius: "8px", maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead sx={{ backgroundColor: Colors.GRAY50 }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, fontSize: '0.8rem' }}>Nome do Usuário</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, fontSize: '0.8rem' }}>Vínculo</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, fontSize: '0.8rem' }}>Posto de Trabalho</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, fontSize: '0.8rem' }}>Resposta Anterior</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: Colors.GRAY800, fontSize: '0.8rem' }} align="center">Status do Questionário</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user: any, idx: number) => (
                      <TableRow key={user.pessoaId || idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 600, color: Colors.GRAY900, fontSize: '0.85rem' }}>
                          {user.nome}
                          {user.cpf && (
                            <Typography variant="caption" sx={{ display: 'block', color: Colors.GRAY500, fontWeight: 400, fontSize: '0.75rem' }}>
                              CPF: {user.cpf}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.tipo} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.7rem', 
                              fontWeight: 600,
                              height: '22px',
                              borderColor: user.tipo === 'Colaborador' ? Colors.SPM600 : Colors.GRAY400,
                              color: user.tipo === 'Colaborador' ? Colors.SPM600 : Colors.GRAY700
                            }} 
                          />
                        </TableCell>
                        <TableCell sx={{ color: Colors.GRAY700, fontSize: '0.8rem' }}>
                          {user.postoTrabalho}
                        </TableCell>
                        <TableCell sx={{ color: Colors.GRAY700, fontSize: '0.8rem' }}>
                          {user.dataUltimaAvaliacaoAnterior || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {user.realizouNovamente ? (
                            <Chip
                              icon={<CheckCircleOutlineIcon style={{ color: '#137333', fontSize: 14 }} />}
                              label={`Preencheu em ${user.dataNovaAvaliacao || ''}`}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: '24px',
                                backgroundColor: '#E6F4EA',
                                color: '#137333',
                                border: '1px solid #CEEAD6'
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<HourglassEmptyIcon style={{ color: '#C5221F', fontSize: 14 }} />}
                              label="Pendente"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: '24px',
                                backgroundColor: '#FCE8E6',
                                color: '#C5221F',
                                border: '1px solid #FAD2CF'
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                py={4} 
                sx={{ border: `1px dashed ${Colors.GRAY300}`, borderRadius: "8px", backgroundColor: Colors.GRAY50 }}
              >
                <GroupIcon sx={{ fontSize: 36, color: Colors.GRAY400, mb: 0.5 }} />
                <Typography variant="body2" sx={{ color: Colors.GRAY600, fontWeight: 600 }}>
                  Nenhum usuário encontrado
                </Typography>
                <Typography variant="caption" sx={{ color: Colors.GRAY500 }}>
                  {userSearchQuery ? "Tente alterar o termo da busca." : "Nenhum usuário ativo atende aos critérios desta parametrização."}
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
            <Button
              variant="contained"
              onClick={() => setImpactedModalOpen(false)}
              sx={{
                borderRadius: '8px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.85rem',
                backgroundColor: Colors.SPM600,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: Colors.SPM700,
                  boxShadow: 'none'
                }
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </GeneralContainer>
    </ContainerWithSidebar>
  );
};

export default ReaplicacaoQuestionario;
